// Knowledge base limits and budgets — docs/plans/2026-10-02-kennisbank-ontwerp.md
// §4 (grenzen), §5 (always_load), §7.1 (run-tools) and §7.2 (prompt).
// One place, so API, worker, web and the prompt composer agree.

/** Max size of one page's stored text (frontmatter + body, UTF-8 bytes). */
export const KB_MAX_PAGE_BYTES = 64 * 1024

/**
 * Max size of a page's frontmatter once parsed (`JSON.stringify`, UTF-8
 * bytes). YAML anchors/aliases are refused outright, so this bounds what
 * lands in the jsonb column and the prompt.
 */
export const KB_MAX_FRONTMATTER_BYTES = 8 * 1024

/** Max number of pages (all spaces together) per workspace — live pages plus proposal placeholders. */
export const KB_MAX_PAGES_PER_WORKSPACE = 5000

/** Max path length (characters), including the `.md` extension. */
export const KB_MAX_PATH_LENGTH = 200

/** Max folder depth: `a/b/c.md` has depth 3. */
export const KB_MAX_PATH_DEPTH = 8

/** `summary` is cut to this many characters (frontmatter or derived). */
export const KB_SUMMARY_MAX_LENGTH = 300

/** Total `always_load` text per space, in (estimated) tokens. */
export const KB_ALWAYS_LOAD_TOKEN_BUDGET = 4000

/** Prompt index (`path — summary` lines) budget, in tokens (§7.2). */
export const KB_INDEX_TOKEN_BUDGET = 1500

/** Number of "probably relevant" pages the prompt lists (§7.2 step 3). */
export const KB_RELEVANT_PAGES_LIMIT = 5

/** `kb_write` calls (revisions + proposals) per run. */
export const KB_MAX_WRITES_PER_RUN = 20

/** `kb_list` returns at most this many entries. */
export const KB_LIST_MAX_ENTRIES = 200

/** `kb_search` limit ceiling (and default). */
export const KB_SEARCH_MAX_LIMIT = 20
export const KB_SEARCH_DEFAULT_LIMIT = 10

/** `kb_for_paths`: at most this many file paths per call. */
export const KB_FOR_PATHS_MAX_FILES = 50

/** Soft-deleted pages are purged after this many days. */
export const KB_DELETED_RETENTION_DAYS = 30

/**
 * Workspace-space folders with per-role / per-agent knowledge (§9.2):
 * `roles/<role>/` and `agents/<agent>/`. They load in full into that
 * role's/agent's runs, with their own budget (`KB_ROLE_AGENT_TOKEN_BUDGET`).
 */
export const KB_ROLES_FOLDER = 'roles/'
export const KB_AGENTS_FOLDER = 'agents/'

/** Token budget for the running role's and agent's own folders, separate from `KB_ALWAYS_LOAD_TOKEN_BUDGET`. */
export const KB_ROLE_AGENT_TOKEN_BUDGET = 2000

/** Stale detection looks at no more than this many changed files per run. */
export const KB_STALE_MAX_CHANGED_FILES = 2000

/** The folder whose pages count as human-approved project rules (§3.8). */
export const KB_CONVENTIONS_FOLDER = 'conventions/'

/**
 * Rough token estimate (≈ 4 characters per token). Good enough for
 * budgets; never use it for billing.
 */
export function estimateKbTokens(text: string): number {
  return Math.ceil(text.length / 4)
}

/** UTF-8 byte length without Node's Buffer (browser-safe). */
export function kbByteLength(text: string): number {
  return new TextEncoder().encode(text).length
}
